import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from './api'
import type { LiveEventFrame, LiveMessage, LivePositionFrame, VehicleState } from './types'

export type FeedStatus = 'connecting' | 'live' | 'stale' | 'offline'

interface LiveFeed {
  status: FeedStatus
  /** Monotonic counter of frames received; drives the telemetry strip. */
  packets: number
  /** Rolling packets-per-second, smoothed so the readout does not flicker. */
  rate: number
  lastPacketAt: number | null
  positions: Map<string, LivePositionFrame>
  events: LiveEventFrame[]
  states: Map<string, VehicleState>
  clearEvents: () => void
}

interface Options {
  /** Narrow the subscription to specific vehicles, so a detail page does not
   *  receive the whole fleet's traffic. */
  vehicleIds?: string[]
  enabled?: boolean
  onEvent?: (e: LiveEventFrame) => void
}

/**
 * Subscribes to the server's live telemetry feed.
 *
 * Reconnects with exponential backoff and full jitter: when a node restarts,
 * every browser watching the map would otherwise reconnect on the same
 * schedule and hammer the replacement.
 */
export function useLiveFeed({ vehicleIds, enabled = true, onEvent }: Options = {}): LiveFeed {
  const [status, setStatus] = useState<FeedStatus>('connecting')
  const [packets, setPackets] = useState(0)
  const [rate, setRate] = useState(0)
  const [lastPacketAt, setLastPacketAt] = useState<number | null>(null)
  const [, forceRender] = useState(0)

  // The maps are refs, not state: at fleet scale a setState per packet would
  // re-render the tree hundreds of times a second. A coalesced render runs on
  // an interval instead.
  const positions = useRef(new Map<string, LivePositionFrame>())
  const states = useRef(new Map<string, VehicleState>())
  const events = useRef<LiveEventFrame[]>([])
  const dirty = useRef(false)

  const socket = useRef<WebSocket | null>(null)
  const attempt = useRef(0)
  const reconnectTimer = useRef<number | undefined>(undefined)
  const windowCount = useRef(0)
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  const key = vehicleIds?.length ? vehicleIds.join(',') : ''

  const connect = useCallback(() => {
    const token = getAccessToken()
    if (!token) {
      setStatus('offline')
      return
    }

    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const params = new URLSearchParams({ access_token: token })
    if (key) params.set('vehicleIds', key)

    const ws = new WebSocket(`${proto}://${location.host}/api/v1/ws?${params}`)
    socket.current = ws
    setStatus('connecting')

    ws.onopen = () => {
      attempt.current = 0
      setStatus('live')
    }

    ws.onmessage = (ev) => {
      let msg: LiveMessage
      try {
        msg = JSON.parse(ev.data as string) as LiveMessage
      } catch {
        return
      }

      switch (msg.type) {
        case 'position': {
          const p = msg.data
          positions.current.set(p.vehicleId, p)
          // A moving vehicle implies its state even before the server's own
          // state message arrives, which keeps the map's colours honest.
          if (p.speedKph != null) {
            states.current.set(
              p.vehicleId,
              p.speedKph >= 5 ? 'moving' : p.ignition ? 'idle' : 'stopped',
            )
          }
          break
        }
        case 'event': {
          const e = msg.data
          events.current = [e, ...events.current].slice(0, 200)
          onEventRef.current?.(e)
          break
        }
        case 'vehicle_state': {
          states.current.set(msg.data.vehicleId, msg.data.state)
          break
        }
        case 'hello':
          return
      }

      dirty.current = true
      windowCount.current++
      setPackets((n) => n + 1)
      setLastPacketAt(Date.now())
    }

    ws.onclose = () => {
      socket.current = null
      setStatus('offline')
      scheduleReconnect()
    }

    ws.onerror = () => {
      ws.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const scheduleReconnect = useCallback(() => {
    const n = Math.min(attempt.current++, 6)
    const ceiling = Math.min(1000 * 2 ** n, 30_000)
    const delay = Math.random() * ceiling
    window.clearTimeout(reconnectTimer.current)
    reconnectTimer.current = window.setTimeout(() => connect(), delay)
  }, [connect])

  useEffect(() => {
    if (!enabled) {
      socket.current?.close()
      return
    }
    connect()

    return () => {
      window.clearTimeout(reconnectTimer.current)
      const ws = socket.current
      socket.current = null
      // Clear the handler first: the close below would otherwise schedule a
      // reconnect for a feed that is being torn down.
      if (ws) {
        ws.onclose = null
        ws.close()
      }
    }
  }, [connect, enabled])

  // Coalesced render + rate sampling.
  useEffect(() => {
    const tick = window.setInterval(() => {
      if (dirty.current) {
        dirty.current = false
        forceRender((n) => n + 1)
      }

      const observed = windowCount.current
      windowCount.current = 0
      // Exponential smoothing; a hard count jumps around too much to read.
      setRate((prev) => prev * 0.6 + observed * 0.4)

      setStatus((s) => {
        if (s === 'offline' || s === 'connecting') return s
        return observed === 0 && socket.current?.readyState === WebSocket.OPEN
          ? staleIfQuiet(lastPacketAt)
          : 'live'
      })
    }, 1000)

    return () => window.clearInterval(tick)
  }, [lastPacketAt])

  const clearEvents = useCallback(() => {
    events.current = []
    forceRender((n) => n + 1)
  }, [])

  return {
    status,
    packets,
    rate,
    lastPacketAt,
    positions: positions.current,
    events: events.current,
    states: states.current,
    clearEvents,
  }
}

/** A connected socket with no traffic for 45s is suspicious but not dead: a
 *  small fleet parked overnight looks exactly like a broken feed otherwise. */
function staleIfQuiet(last: number | null): FeedStatus {
  if (last == null) return 'live'
  return Date.now() - last > 45_000 ? 'stale' : 'live'
}
